import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Grid3X3,
  List,
  Clock,
  FolderKanban,
  ArrowUpRight,
  MoreVertical,
  Settings,
  Trash2,
  Copy,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Dropdown } from '@/components/ui/Dropdown';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { ConfirmModal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { useProjects, useDeleteProject } from '@/hooks/useProject';
import { useMultipleProjectStatuses } from '@/hooks/useProjectStatus';
import { useAuthStore } from '@/stores/authStore';
import { formatRelativeTime } from '@/utils/formatters';
import { cn } from '@/utils/cn';
import type { ProjectListItem } from '@/types';

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data, isLoading } = useProjects();
  const deleteProject = useDeleteProject();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectToDelete, setProjectToDelete] = useState<ProjectListItem | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const projects = data?.items || [];

  // Get real-time status for all projects
  const projectSlugs = useMemo(() => projects.map(p => p.slug), [projects]);
  const { getStatus } = useMultipleProjectStatuses(projectSlugs, {
    pollInterval: 15000, // Poll every 15 seconds for dashboard
    enabled: projects.length > 0,
  });

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await deleteProject.mutateAsync({
        slug: projectToDelete.slug,
        id: projectToDelete.id,
      });
      setProjectToDelete(null);
      setDeleteConfirmation('');
    } catch (error) {
      // Error handled by the mutation
    }
  };

  const expectedDeleteConfirmation = projectToDelete ? `${projectToDelete.slug}.on.applantus` : '';

  const getProjectMenuItems = (project: ProjectListItem) => [
    {
      id: 'open',
      label: 'Open Project',
      icon: <ArrowUpRight className="w-4 h-4" />,
      onClick: () => navigate(`/projects/${project.slug}`),
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-4 h-4" />,
      onClick: () => navigate(`/projects/${project.slug}/settings`),
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: <Copy className="w-4 h-4" />,
      onClick: () => {
        // TODO: Implement duplicate
      },
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      danger: true,
      onClick: () => setProjectToDelete(project),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Welcome back, {user?.displayName?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Here's an overview of your projects
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard
          label="Total Projects"
          value={data?.totalCount || 0}
          icon={<FolderKanban className="w-5 h-5" />}
        />
        <StatCard
          label="Running"
          value={projects.filter((p) => p.status === 'Running').length}
          icon={<div className="w-2 h-2 rounded-full bg-green-500" />}
        />
        <StatCard
          label="Last Activity"
          value={projects.length > 0 ? formatRelativeTime(projects[0].updatedAt) : 'N/A'}
          icon={<Clock className="w-5 h-5" />}
        />
      </div>

      {/* Projects Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Your Projects
          </h2>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-dark-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-1.5 rounded',
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-dark-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-1.5 rounded',
                  viewMode === 'list'
                    ? 'bg-white dark:bg-dark-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* New Project */}
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/projects/new')}
            >
              New Project
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className={cn(
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
              : 'space-y-3'
          )}>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredProjects.length === 0 && (
          <EmptyState
            icon={<FolderKanban className="w-12 h-12" />}
            title={searchQuery ? 'No projects found' : 'No projects yet'}
            description={
              searchQuery
                ? 'Try adjusting your search query'
                : 'Create your first project to get started building with AI'
            }
            action={
              !searchQuery
                ? {
                    label: 'Create Project',
                    onClick: () => navigate('/projects/new'),
                  }
                : undefined
            }
          />
        )}

        {/* Projects Grid/List */}
        {!isLoading && filteredProjects.length > 0 && (
          <div
            className={cn(
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                : 'space-y-3'
            )}
          >
            {filteredProjects.map((project) =>
              viewMode === 'grid' ? (
                <ProjectCard
                  key={project.id}
                  project={project}
                  menuItems={getProjectMenuItems(project)}
                  realTimeStatus={getStatus(project.slug)}
                />
              ) : (
                <ProjectListItemComponent
                  key={project.id}
                  project={project}
                  menuItems={getProjectMenuItems(project)}
                  realTimeStatus={getStatus(project.slug)}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!projectToDelete}
        onClose={() => {
          setProjectToDelete(null);
          setDeleteConfirmation('');
        }}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        message={
          <div className="space-y-4">
            <p>
              This will permanently delete <strong>{projectToDelete?.name}</strong> and all associated data including:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
              <li>All project files and code</li>
              <li>Docker containers and images</li>
              <li>Environment variables</li>
              <li>AI conversation history</li>
            </ul>
            <div className="pt-2 border-t border-gray-200 dark:border-dark-700">
              <Input
                label={
                  <span>
                    Type <code className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-mono text-sm">{expectedDeleteConfirmation}</code> to confirm
                  </span>
                }
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder={expectedDeleteConfirmation}
                className={deleteConfirmation === expectedDeleteConfirmation ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {deleteConfirmation.length > 0 && deleteConfirmation !== expectedDeleteConfirmation && (
                <p className="mt-1 text-sm text-red-500">Confirmation text doesn't match</p>
              )}
            </div>
          </div>
        }
        confirmText="I understand, delete this project"
        variant="danger"
        isLoading={deleteProject.isPending}
        confirmDisabled={deleteConfirmation !== expectedDeleteConfirmation}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}) {
  return (
    <Card className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-400">
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
      </div>
    </Card>
  );
}

function ProjectCard({
  project,
  menuItems,
  realTimeStatus,
}: {
  project: ProjectListItem;
  menuItems: { id: string; label: string; icon?: React.ReactNode; danger?: boolean; onClick: () => void }[];
  realTimeStatus: string | null;
}) {
  const navigate = useNavigate();

  // Use real-time status if available, otherwise fall back to database status
  const displayStatus = realTimeStatus || project.status;

  return (
    <Card
      hover
      onClick={() => navigate(`/projects/${project.slug}`)}
      className="relative"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
            <FolderKanban className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">{project.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{project.slug}</p>
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={
              <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-700">
                <MoreVertical className="w-4 h-4 text-gray-400" />
              </button>
            }
            items={menuItems}
            align="right"
          />
        </div>
      </div>

      {project.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
          {project.description}
        </p>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-dark-700">
        <StatusBadge status={displayStatus} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Updated {formatRelativeTime(project.updatedAt)}
        </span>
      </div>
    </Card>
  );
}

function ProjectListItemComponent({
  project,
  menuItems,
  realTimeStatus,
}: {
  project: ProjectListItem;
  menuItems: { id: string; label: string; icon?: React.ReactNode; danger?: boolean; onClick: () => void }[];
  realTimeStatus: string | null;
}) {
  const navigate = useNavigate();

  // Use real-time status if available, otherwise fall back to database status
  const displayStatus = realTimeStatus || project.status;

  return (
    <div
      onClick={() => navigate(`/projects/${project.slug}`)}
      className="flex items-center gap-4 p-4 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 hover:border-gray-300 dark:hover:border-dark-600 cursor-pointer transition-colors"
    >
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0">
        <FolderKanban className="w-5 h-5 text-white" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-gray-900 dark:text-white truncate">
            {project.name}
          </h3>
          <StatusBadge status={displayStatus} />
        </div>
        {project.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {project.description}
          </p>
        )}
      </div>

      <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
        {formatRelativeTime(project.updatedAt)}
      </span>

      <div onClick={(e) => e.stopPropagation()}>
        <Dropdown
          trigger={
            <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-dark-700">
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          }
          items={menuItems}
          align="right"
        />
      </div>
    </div>
  );
}
